// @givmo/compliance — what a filing's "Corrections and changes" says (CCM-R1-INTAKE-HISTORY;
// DESIGN-NOTE-1b §4.2; the source-notes design note §2; Jonathan's rulings 8 and 9, 2026-10-01).
// Pure functions over decoded records; no React, no reads.
//
// ══ ITS OWN LIST, AFTER THE YEARS ══════════════════════════════════════════════════════════════
//
// History is the filing's years first. A correction is a different kind of entry, so it gets its own
// list after the year tiles, newest first, and never reads as a filing year (ruling 8). Each entry is
// one act: what changed, from what to what; then, quietly, who and when, and the person's own words
// where they gave any.
//
// ══ WHAT COUNTS AS AN ACT HERE ═════════════════════════════════════════════════════════════════
//
//   • A NUMBER CORRECTED: on a library filing, each correction recorded beside the import that
//     belongs to it (append-only; the import keeps what was first entered, which none of them
//     touches); on a filing the organization records itself, each change its own history keeps
//     (CCM-R1-OWN-FILINGS-HISTORY: append-only too, served with the number first entered). Both
//     come here in one shape, so the two read alike. A number cleared has no ruled words: skipped.
//   • THE FISCAL YEAR END CHANGED: only on a filing whose dates are worked out from it; only when it
//     was recorded after the filing was added, since otherwise "worked out again" is not true of this
//     filing; and only when the value moved, since recording the same value again writes a new row
//     and changes nothing.
//   • A REGISTRATION DATE OR A LICENSE'S EXPIRATION DATE CHANGED: the filing's own events that carry
//     a `from` value. The first recording is not a change, and an equal value re-recorded writes no
//     event at all (CCM-BE14).
//
// ⚠ "THIS FILING'S DATES WERE WORKED OUT AGAIN." ONLY WHERE IT IS TRUE OF THIS FILING (the
// orchestrator, 2026-10-01 13:24 PDT): only when the filing's dates are counted from the date that
// changed. The fiscal year end's entry shows only there at all. A changed date's entry always shows,
// since it is the filing's own record, but says only who and when ("Changed by … on …."), a subset
// of the ruled words, where nothing on the filing is counted from it: a one-time registration's date,
// say. Which shapes read which date is the backend's own arithmetic (`compute_deadline_occurrence`).
//
// ⚠ NO DASH IN A SENTENCE. An act whose words cannot all be read is left out rather than printed
// with a gap (the orchestrator's ruling on Q-D6, 2026-10-01): a correction with no first number says
// "corrected to …", the approved sentence's own words, and nothing here ever says "from —".

import {
  AMENDABLE_IMPORT_FIELD,
  displayComplianceDate,
  displayLocalDate,
  type ObligationEvent,
  type ProfileParameter,
  type RegistrationImportAmendment,
} from './compliance-decode';
import type { OwnFilingNumberCorrection } from './compliance-filings-decode';
import { ANCHOR_LABELS, monthDayWords } from './compliance-panel-model';
import { linkActLine, linkedEntry, unlinkedEntry } from './compliance-vendor-model';

/** The list's heading (ruling 9). */
export const CORRECTIONS_AND_CHANGES = 'Corrections and changes';

/** What a change's second line ends with, where the filing's dates are counted from what changed. */
const WORKED_OUT_AGAIN = 'This filing’s dates were worked out again.';

/**
 * The deadline shapes counted from the fiscal year end: a change to it moves their dates. The
 * backend's `FISCAL_YEAR_PERIOD_TYPES`, the same set whose "Change" is the fiscal year end
 * (`basisChangeFor`), the federal return's included (orchestrator, Q-D1).
 */
const FISCAL_YEAR_BASES: readonly string[] = [
  'fiscal_year_end_plus_interval',
  'fiscal_year_end_nth_month_day',
  'concurrent_with_federal_return',
];

/** Whether a filing's dates are worked out from the fiscal year end, by the shapes it is dated by. */
export function isFiscalYearAnchored(dueDateBases: readonly string[]): boolean {
  return dueDateBases.some((basis) => FISCAL_YEAR_BASES.includes(basis));
}

/** The shapes counted from the registration date's anniversary: they always read it. */
const ANNIVERSARY_BASES: readonly string[] = [
  'anniversary_of_registration',
  'last_day_of_anniversary_month',
];

/**
 * The licence-period shapes (the backend's `LICENCE_PERIOD_TYPES`): counted from the recorded licence
 * expiry, and, while none is recorded, from the registration date advanced by the term (the backend's
 * `_licence_period_end`, the issue-date path).
 */
const LICENCE_PERIOD_BASES: readonly string[] = [
  'license_cycle_expiry',
  'registration_period_end_plus_interval',
];

export interface HistoryEntry {
  key: string;
  /** What changed, from what to what. */
  what: string;
  /** Who and when, and the person's own words where they gave any. */
  how: string;
  /** The act's instant, which orders the list. */
  at: string;
}

/**
 * One change to a filing's number, whichever record keeps it: a library filing's import amendment or
 * an own filing's history entry (CCM-R1-OWN-FILINGS-HISTORY). `key` names the entry on the page.
 */
export interface NumberCorrection {
  key: string;
  previousValue?: string;
  amendedValue?: string;
  reason?: string;
  recordedByUserId?: string;
  recordedAt: string;
}

/** A library filing's corrections: the ones its import's amendments make to the number. */
export function importNumberCorrections(
  amendments: readonly RegistrationImportAmendment[],
): NumberCorrection[] {
  return amendments
    .filter((amendment) => amendment.fieldKey === AMENDABLE_IMPORT_FIELD)
    .map((amendment) => ({
      key: `amendment:${amendment.amendmentId}`,
      previousValue: amendment.previousValue,
      amendedValue: amendment.amendedValue,
      reason: amendment.reason,
      recordedByUserId: amendment.recordedByUserId,
      recordedAt: amendment.recordedAt,
    }));
}

/** An own filing's corrections: each change to the number its history keeps. */
export function ownNumberCorrections(
  corrections: readonly OwnFilingNumberCorrection[],
): NumberCorrection[] {
  return corrections.map((correction) => ({
    key: `own-correction:${correction.correctionId}`,
    previousValue: correction.previousValue,
    amendedValue: correction.amendedValue,
    reason: correction.reason,
    recordedByUserId: correction.recordedByUserId,
    recordedAt: correction.recordedAt ?? '',
  }));
}

export interface HistoryInput {
  /** The filing's own word for its number ("Registration number", D.C.'s "License number", "Number"). */
  numberLabel: string;
  /** The changes to the filing's number, oldest or newest first, from either record. */
  numberCorrections: readonly NumberCorrection[];
  /** Every recorded fiscal year end, superseded ones included (`include_superseded`). */
  fiscalYearEnds: readonly ProfileParameter[];
  /** The deadline shapes this filing is dated by (its years' and occurrences' `due_date_basis`). */
  dueDateBases: readonly string[];
  /** When the filing was added (its confirmation). Without it, no fiscal-year-end entry. */
  addedAt?: string;
  /** The filing's own event log. */
  events: readonly ObligationEvent[];
  /** A person's name by user id, or nothing for someone the host cannot name. */
  nameOf: (userId: string) => string | undefined;
  /** Another zone than the viewer's, for the acts' days (tests pin the evening case with it). */
  timeZone?: string;
  /** CCM-MOD5-RECORD-FILING-LINK: a record's title by its id, for the link entries. */
  recordTitle?: (vendorId: string) => string | undefined;
}

/** An act's instant in milliseconds, NaN when it cannot be read. */
function instant(iso: string): number {
  return Date.parse(iso);
}

/** "Corrected by Maya Solberg on Sep 24, 2026: “…”", and its forms without words or a name (ruling 9). */
function correctedLine(name: string | undefined, on: string, reason: string | undefined): string {
  const words = reason?.trim();
  const said = words !== undefined && words.length > 0 ? `: “${words}”` : '';
  // A person the host cannot name still keeps their own words (the orchestrator, Q-H2).
  return name !== undefined ? `Corrected by ${name} on ${on}${said}` : `Corrected on ${on}${said}`;
}

/**
 * "Changed by Maya Solberg on Sep 24, 2026. This filing’s dates were worked out again." (ruling 9),
 * or its first sentence alone where nothing on the filing is counted from what changed.
 */
function changedLine(name: string | undefined, on: string, workedOutAgain: boolean): string {
  const said = name !== undefined ? `Changed by ${name} on ${on}.` : `Changed on ${on}.`;
  return workedOutAgain ? `${said} ${WORKED_OUT_AGAIN}` : said;
}

function nameOf(input: HistoryInput, userId: string | undefined): string | undefined {
  if (userId === undefined || userId.length === 0) return undefined;
  const name = input.nameOf(userId)?.trim();
  return name !== undefined && name.length > 0 ? name : undefined;
}

function numberCorrections(input: HistoryInput): HistoryEntry[] {
  return input.numberCorrections.flatMap((correction) => {
    const to = correction.amendedValue?.trim();
    const on = displayLocalDate(correction.recordedAt, input.timeZone);
    // A correction always names its new value; one that cannot be read, or cannot be dated, is left
    // out rather than shown with a gap. So is a number cleared (an own filing's, through the
    // connector): there are no ruled words for a removal (the orchestrator, Q3, 2026-10-01).
    if (to === undefined || to.length === 0 || on === '—') return [];
    const from = correction.previousValue?.trim();
    return [
      {
        key: correction.key,
        what:
          from !== undefined && from.length > 0
            ? `${input.numberLabel} corrected from ${from} to ${to}`
            : `${input.numberLabel} corrected to ${to}`,
        how: correctedLine(nameOf(input, correction.recordedByUserId), on, correction.reason),
        at: correction.recordedAt,
      },
    ];
  });
}

function fiscalYearEndChanges(input: HistoryInput): HistoryEntry[] {
  const added = input.addedAt !== undefined ? instant(input.addedAt) : Number.NaN;
  if (!isFiscalYearAnchored(input.dueDateBases) || Number.isNaN(added)) return [];
  const chain = input.fiscalYearEnds
    .filter((parameter) => parameter.parameterKey === 'fiscal_year_end')
    .filter((parameter) => !Number.isNaN(instant(parameter.recordedAt)))
    .sort((a, b) => instant(a.recordedAt) - instant(b.recordedAt));
  const entries: HistoryEntry[] = [];
  chain.forEach((parameter, index) => {
    const before = index > 0 ? chain[index - 1] : undefined;
    // The first value recorded is no change, and an equal value recorded again moved nothing.
    if (before === undefined || before.recordedValue.trim() === parameter.recordedValue.trim()) {
      return;
    }
    // Only a change made after the filing was added worked its dates out again.
    if (instant(parameter.recordedAt) <= added) return;
    const from = monthDayWords(before.recordedValue);
    const to = monthDayWords(parameter.recordedValue);
    const on = displayLocalDate(parameter.recordedAt, input.timeZone);
    if (from === undefined || to === undefined || on === '—') return;
    entries.push({
      key: `fiscal-year-end:${parameter.parameterId}`,
      what: `Fiscal year end changed from ${from} to ${to}`,
      how: changedLine(nameOf(input, parameter.recordedByUserId), on, true),
      at: parameter.recordedAt,
    });
  });
  return entries;
}

/** The two dates a filing's own events change, each with its detail keys and its label. */
const DATE_EVENTS: Readonly<
  Record<string, { from: string; to: string; label: string } | undefined>
> = {
  registration_date_recorded: {
    from: 'from_registration_date',
    to: 'to_registration_date',
    label: ANCHOR_LABELS['registration-date'],
  },
  license_expiry_recorded: {
    from: 'from_license_expires_on',
    to: 'to_license_expires_on',
    label: ANCHOR_LABELS['license-expiry'],
  },
};

/**
 * Whether this filing's dates are counted from the date an event changed, at the time it changed:
 * a registration date by the anniversary shapes, and by the licence-period shapes while no licence
 * expiry was on record (an expiry recorded in the same act counts as on record); a licence expiry by
 * the licence-period shapes.
 */
function countsFrom(input: HistoryInput, event: ObligationEvent): boolean {
  const has = (bases: readonly string[]) =>
    input.dueDateBases.some((basis) => bases.includes(basis));
  if (event.eventType === 'license_expiry_recorded') return has(LICENCE_PERIOD_BASES);
  const expiryOnRecord = input.events.some(
    (other) =>
      other.eventType === 'license_expiry_recorded' &&
      instant(other.occurredAt) <= instant(event.occurredAt),
  );
  return has(ANNIVERSARY_BASES) || (has(LICENCE_PERIOD_BASES) && !expiryOnRecord);
}

function dateChanges(input: HistoryInput): HistoryEntry[] {
  return input.events.flatMap((event) => {
    const shape = DATE_EVENTS[event.eventType];
    if (shape === undefined) return [];
    const fromValue = event.detail[shape.from];
    const toValue = event.detail[shape.to];
    // The first recording carries no `from`: it is not a change.
    if (typeof fromValue !== 'string' || typeof toValue !== 'string') return [];
    const from = displayComplianceDate(fromValue);
    const to = displayComplianceDate(toValue);
    const on = displayLocalDate(event.occurredAt, input.timeZone);
    if (from === '—' || to === '—' || on === '—') return [];
    return [
      {
        key: `event:${event.eventId}`,
        what: `${shape.label} changed from ${from} to ${to}`,
        how: changedLine(nameOf(input, event.actorUserId), on, countsFrom(input, event)),
        at: event.occurredAt,
      },
    ];
  });
}

/** The filing's corrections and changes, newest first. Empty is the ordinary case. */
/**
 * CCM-MOD5-RECORD-FILING-LINK (D-CCM-97): the filing's link to the organization's record and its
 * undoing, from the `vendor_record_linked` and `vendor_record_unlinked` events: "Linked to your
 * record {record}" over who and when, in the act's own channel. An entry whose record or day
 * can't be read is left out.
 */
function linkChanges(input: HistoryInput): HistoryEntry[] {
  return input.events.flatMap((event) => {
    const linked = event.eventType === 'vendor_record_linked';
    if (!linked && event.eventType !== 'vendor_record_unlinked') return [];
    const vendorId = event.detail.vendor_id;
    const title = typeof vendorId === 'string' ? input.recordTitle?.(vendorId) : undefined;
    const on = displayLocalDate(event.occurredAt, input.timeZone);
    if (title === undefined || on === '—') return [];
    const name = nameOf(input, event.actorUserId);
    const channel = event.detail.channel;
    return [
      {
        key: `event:${event.eventId}`,
        what: linked ? linkedEntry('record', title) : unlinkedEntry('record', title),
        how: linkActLine(linked ? 'Linked' : 'Unlinked', {
          day: on,
          ...(name !== undefined ? { name } : {}),
          ...(typeof channel === 'string' ? { channel } : {}),
        }),
        at: event.occurredAt,
      },
    ];
  });
}

export function historyEntries(input: HistoryInput): HistoryEntry[] {
  return [
    ...numberCorrections(input),
    ...fiscalYearEndChanges(input),
    ...dateChanges(input),
    ...linkChanges(input),
  ].sort((a, b) => instant(b.at) - instant(a.at));
}

/**
 * The line beside a corrected number (CCM-WEB2's own words, in the panel's number section; ruling 8):
 * what was first entered stays visible. Only where a first number exists and it differs from the
 * number shown, so it never says "Corrected from —" and never names the number it stands beside.
 */
export function correctedFromLine(
  firstEntered: string | undefined,
  shown: string | undefined,
): string | undefined {
  const first = firstEntered?.trim();
  if (first === undefined || first.length === 0 || shown === undefined || shown === first) {
    return undefined;
  }
  return `Corrected from ${first}, which is what you first entered.`;
}
